/*
 * LanguageProvider
 *
 * this component connects the redux state language locale to the
 * IntlProvider component and i18n messages (loaded from `app/translations`)
 */

import { useEffect, ReactNode } from 'react';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import { IntlProvider, useIntl } from 'react-intl';
import { DEFAULT_LOCALE } from 'services/Language/actions';
import { selectLocale } from 'services/Language/selectors';
import { setLocale as setYupLocale } from 'yup';

import { YUP_I18N } from 'constants/languages.constants';

const defaultLanguageRichTextFormatters = {
  lowercase: (chunks) => chunks.map((chunk) => chunk.toLowerCase()),
  capitalize: (chunks) => chunks.map((chunk) => chunk.charAt(0).toUpperCase() + chunk.slice(1).toLowerCase()),
  strong: (chunks) => <strong>{chunks}</strong>,
  span: (chunks) => <span>{chunks}</span>,
  b: (chunks) => <strong>{chunks}</strong>,
};

const IntlChangeEffect = () => {
  const { locale, formatMessage } = useIntl();

  useEffect(() => {
    setYupLocale(YUP_I18N(formatMessage));
  }, [locale, formatMessage]);

  return null;
};

const LanguageProvider = ({
  locale,
  messages,
  children,
}: {
  locale: string;
  messages: object;
  children: ReactNode;
}) => (
  <IntlProvider
    defaultLocale={DEFAULT_LOCALE}
    locale={locale}
    messages={messages[locale.substring(0, 2)]}
    defaultRichTextElements={defaultLanguageRichTextFormatters}
    onWarn={(warning) => {
      if (typeof __TESTING__ === 'undefined' && process.env.NODE_ENV !== 'production') {
        // Ignore react-intl warning in production and tests.
        console.warn(warning);
      }
    }}
  >
    <IntlChangeEffect />
    {children}
  </IntlProvider>
);

const mapStateToProps = createSelector(selectLocale(), (locale) => ({
  locale,
}));

export default connect(mapStateToProps)(LanguageProvider);
